import { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/authContext';
import useEmailMarketing from '../../hooks/useEmailMarketing';
import QuickActionsWidget from '../EmailMarketing/dashboard/QuickActionsWidget';
import FlowStatusWidget from '../EmailMarketing/dashboard/FlowStatusWidget';
import AudienceWidget from '../EmailMarketing/dashboard/AudienceWidget';
import AutomationWidget from '../EmailMarketing/dashboard/AutomationWidget';
import VoucherStatsWidget from '../EmailMarketing/dashboard/VoucherStatsWidget';
import NewContactsWidget from '../EmailMarketing/dashboard/NewContactsWidget';
import FunnelKpisWidget from '../EmailMarketing/dashboard/FunnelKpisWidget';
import FraudAlertsWidget from '../EmailMarketing/dashboard/FraudAlertsWidget';
import ActivationIssuesDialog from '../EmailMarketing/dashboard/ActivationIssuesDialog';
import FlowEmptyState from '../EmailMarketing/dashboard/FlowEmptyState';
import NameDialog from '../MailEditor/NameDialog';

// Default dashboard range for the "Neue Kontakte" widget: the last 30 days.
const defaultRange = () => ({
  from: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
  to: dayjs().format('YYYY-MM-DD'),
});

// Pulls the activation issues out of a 422 error; the backend nests them
// either directly in err.data or under err.data.data / err.data.issues.
const extractActivationIssues = err => {
  const data = err?.data;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.issues)) return data.issues;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

// E-mail marketing dashboard: works for any flow. A flow selector drives all
// widgets (status, audience, automation health, vouchers, new contacts) plus
// quick actions. Mails are sent by GetResponse — the flow orchestrates
// vouchers and the GetResponse data exchange.
export default function EmailMarketingDashboard() {
  const authCtx = useAuth();
  const navigate = useNavigate();
  const {
    getDashboard,
    createFlow,
    activateFlow,
    pauseFlow,
    getFunnelKpis,
    getFraudAlerts,
    patchFraudAlert,
  } = useEmailMarketing();

  const [dashboard, setDashboard] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [dashboardError, setDashboardError] = useState(null);
  const [range, setRange] = useState(defaultRange);
  const [selectedFlowId, setSelectedFlowId] = useState(null);
  const [actionBusy, setActionBusy] = useState(null);
  const [newFlowOpen, setNewFlowOpen] = useState(false);
  const [activationIssues, setActivationIssues] = useState(null);
  const [funnelKpis, setFunnelKpis] = useState(null);
  const [funnelKpisLoading, setFunnelKpisLoading] = useState(true);
  const [funnelKpisError, setFunnelKpisError] = useState(null);
  const [fraudAlerts, setFraudAlerts] = useState([]);
  const [fraudLoading, setFraudLoading] = useState(true);
  const [fraudError, setFraudError] = useState(null);
  const [fraudBusyId, setFraudBusyId] = useState(null);

  const canUpdate = authCtx.checkRight('emailMarketing:update');
  const canActivate = authCtx.checkRight('emailMarketing:activate');

  const flow = dashboard?.flow || null;
  const flowId = flow?._id || null;
  const flows = Array.isArray(dashboard?.flows) ? dashboard.flows : [];

  const loadDashboard = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setDashboardLoading(true);
      try {
        const data = await getDashboard({
          from: range.from,
          to: range.to,
          flowId: selectedFlowId || undefined,
        });
        // A null result means the request was stored for re-authentication.
        if (data) {
          setDashboard(data);
          setDashboardError(null);
          // Default-select the first flow when the backend could not resolve
          // one but other flows exist, so every widget has a flow to work with.
          if (!selectedFlowId && !data.flow && Array.isArray(data.flows) && data.flows.length > 0) {
            setSelectedFlowId(data.flows[0]._id);
          }
        }
      } catch (err) {
        setDashboardError(err);
        toast.error('Fehler beim Laden des E-Mail-Marketing-Dashboards: ' + err.message);
      } finally {
        if (!silent) setDashboardLoading(false);
      }
    },
    [getDashboard, range.from, range.to, selectedFlowId]
  );

  const loadFunnelKpis = useCallback(async () => {
    setFunnelKpisLoading(true);
    try {
      const data = await getFunnelKpis();
      if (data) {
        setFunnelKpis(data);
        setFunnelKpisError(null);
      }
    } catch (err) {
      setFunnelKpisError(err);
    } finally {
      setFunnelKpisLoading(false);
    }
  }, [getFunnelKpis]);

  const loadFraudAlerts = useCallback(async () => {
    setFraudLoading(true);
    try {
      const data = await getFraudAlerts();
      if (data) {
        setFraudAlerts(data.alerts || []);
        setFraudError(null);
      }
    } catch (err) {
      setFraudError(err);
    } finally {
      setFraudLoading(false);
    }
  }, [getFraudAlerts]);

  const handleFraudPatch = useCallback(
    async (alert, body) => {
      setFraudBusyId(alert._id);
      try {
        const result = await patchFraudAlert(alert._id, body);
        toast.success(
          body.status === 'resolved'
            ? result?.voucherLocked
              ? 'Alert geklärt, Gutschein-Code gesperrt.'
              : 'Alert als geklärt markiert.'
            : 'Alert verworfen.'
        );
        await loadFraudAlerts();
      } catch (err) {
        toast.error('Fehler beim Aktualisieren des Fraud-Alerts: ' + err.message);
      } finally {
        setFraudBusyId(null);
      }
    },
    [patchFraudAlert, loadFraudAlerts]
  );

  // Initial load; re-runs whenever the range or the selected flow changes.
  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    loadFunnelKpis();
    loadFraudAlerts();
  }, [loadFunnelKpis, loadFraudAlerts]);

  // Auto-refresh the dashboard every 60 seconds, keeping the selected flow.
  useEffect(() => {
    const intervalId = setInterval(() => {
      loadDashboard({ silent: true });
    }, 60000);
    return () => clearInterval(intervalId);
  }, [loadDashboard]);

  const handleSelectFlow = event => {
    setSelectedFlowId(event.target.value);
  };

  // Creates an additional flow and jumps straight into the flow builder; the
  // name comes from the "Neuer Flow" dialog and can be renamed there later.
  const handleCreateFlow = async name => {
    setActionBusy('create');
    try {
      const created = await createFlow({ name });
      if (created?._id) {
        toast.success('Neuer Flow wurde erstellt.');
        navigate(`/emailMarketing/flow/${created._id}`);
      }
    } catch (err) {
      toast.error('Fehler beim Erstellen des Flows: ' + err.message);
      throw err;
    } finally {
      setActionBusy(null);
    }
  };

  const handleEditFlow = () => {
    if (flowId) navigate(`/emailMarketing/flow/${flowId}`);
  };

  const handleToggleFlow = async () => {
    if (!flow) return;
    const isActive = flow.status === 'active';
    setActionBusy('toggle');
    try {
      const result = isActive ? await pauseFlow(flow._id) : await activateFlow(flow._id);
      if (result) toast.success(isActive ? 'Flow wurde pausiert.' : 'Flow wurde aktiviert.');
      await loadDashboard({ silent: true });
    } catch (err) {
      const issues = !isActive && err.statusCode === 422 ? extractActivationIssues(err) : [];
      if (issues.length > 0) {
        setActivationIssues(issues);
      } else {
        toast.error(
          (isActive ? 'Fehler beim Pausieren des Flows: ' : 'Fehler beim Aktivieren des Flows: ') +
            err.message
        );
      }
    } finally {
      setActionBusy(null);
    }
  };

  if (!authCtx.checkRight('emailMarketing:read')) return <Navigate to='/welcome' replace />;

  // Only surface the error state inside the widgets while there is no data yet;
  // background refresh failures keep the last good data visible.
  const widgetError = dashboard ? null : dashboardError;
  const flowMissing =
    !dashboardLoading && !widgetError && dashboard && !flow && flows.length === 0;
  const selectorValue = selectedFlowId || flowId || '';

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          mb: { xs: 2, sm: 2.5 },
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'flex-end',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography variant='h5' sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            E-Mail-Marketing
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Automatische Marketing-Flows im Überblick: Zielgruppe, Gutscheine und der
            GetResponse-Austausch. Die Mails selbst versendet GetResponse.
          </Typography>
        </Box>
        <Stack direction='row' spacing={1.5} alignItems='center' flexWrap='wrap'>
          {flows.length > 0 && (
            <FormControl size='small' sx={{ minWidth: 220 }}>
              <InputLabel id='email-marketing-flow-label'>Flow</InputLabel>
              <Select
                labelId='email-marketing-flow-label'
                label='Flow'
                value={selectorValue}
                onChange={handleSelectFlow}
              >
                {flows.map(f => (
                  <MenuItem key={f._id} value={f._id}>
                    {f.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {canUpdate && !flowMissing && (
            <Button
              variant='outlined'
              size='small'
              startIcon={<AddIcon />}
              disabled={actionBusy === 'create'}
              onClick={() => setNewFlowOpen(true)}
            >
              Neuer Flow
            </Button>
          )}
        </Stack>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          alignItems: 'stretch',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(3, minmax(0, 1fr))',
          },
        }}
      >
        {flowMissing ? (
          <Box sx={{ gridColumn: '1 / -1' }}>
            <FlowEmptyState
              canUpdate={canUpdate}
              busy={actionBusy === 'create'}
              onCreate={() => setNewFlowOpen(true)}
            />
          </Box>
        ) : (
          <>
            <Box>
              <QuickActionsWidget
                flow={flow}
                canUpdate={canUpdate}
                canActivate={canActivate}
                busy={actionBusy}
                onEditFlow={handleEditFlow}
                onToggleFlow={handleToggleFlow}
                loading={dashboardLoading}
                error={widgetError}
                onRetry={() => loadDashboard()}
              />
            </Box>
            <Box>
              <FlowStatusWidget
                flow={flow}
                loading={dashboardLoading}
                error={widgetError}
                onRetry={() => loadDashboard()}
              />
            </Box>
            <Box>
              <AudienceWidget
                audience={dashboard?.audience}
                listName={flow?.listName}
                loading={dashboardLoading}
                error={widgetError}
                onRetry={() => loadDashboard()}
              />
            </Box>
            <Box>
              <AutomationWidget
                automation={dashboard?.automation}
                loading={dashboardLoading}
                error={widgetError}
                onRetry={() => loadDashboard()}
              />
            </Box>
            <Box>
              <VoucherStatsWidget
                vouchers={dashboard?.vouchers}
                loading={dashboardLoading}
                error={widgetError}
                onRetry={() => loadDashboard()}
              />
            </Box>
            <Box sx={{ gridColumn: { sm: 'span 2', lg: 'span 3' } }}>
              <NewContactsWidget
                newContacts={dashboard?.newContacts}
                range={range}
                onRangeChange={setRange}
                loading={dashboardLoading}
                error={widgetError}
                onRetry={() => loadDashboard()}
              />
            </Box>
          </>
        )}

        <Box sx={{ gridColumn: { sm: 'span 2', lg: 'span 2' } }}>
          <FunnelKpisWidget
            kpis={funnelKpis}
            loading={funnelKpisLoading}
            error={funnelKpis ? null : funnelKpisError}
            onRetry={loadFunnelKpis}
          />
        </Box>
        <Box>
          <FraudAlertsWidget
            alerts={fraudAlerts}
            canUpdate={canUpdate}
            busyId={fraudBusyId}
            loading={fraudLoading}
            error={fraudError}
            onRetry={loadFraudAlerts}
            onPatch={handleFraudPatch}
          />
        </Box>
      </Box>

      <NameDialog
        open={newFlowOpen}
        title='Neuen Flow erstellen'
        label='Name des Flows'
        description='Der Flow wird als Entwurf angelegt und direkt im Flow-Builder geöffnet.'
        defaultValue='Neuer Flow'
        confirmLabel='Erstellen'
        onClose={() => setNewFlowOpen(false)}
        onConfirm={handleCreateFlow}
      />
      <ActivationIssuesDialog
        open={Boolean(activationIssues)}
        issues={activationIssues || []}
        onClose={() => setActivationIssues(null)}
      />
    </Box>
  );
}
